import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CustomSelect from '../components/CustomSelect';
import InvoiceViewModalNew from '../components/InvoiceViewModalNew';
import InvoiceDataReview from '../components/InvoiceDataReview';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  EyeIcon,
  TrashIcon,
  DocumentTextIcon,
  CheckIcon,
  ShieldCheckIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const CompactInvoiceForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // State for search filters
  const [searchFilters, setSearchFilters] = useState({
    buyerRegistrationNo: '',
    buyerName: '',
    invoiceNo: '',
    sellerRegistrationNo: '',
    sellerName: '',
    invoiceStatus: '',
    invoiceType: '',
    saleType: '',
    itemSerialNo: '',
    fromDate: '',
    toDate: '',
    transactionType: 'ALL' // ALL, SALES, PURCHASES
  });

  // State for invoices list
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewInvoiceForm, setShowNewInvoiceForm] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Maximum 10 invoices per page

  // Pagination calculations
  const totalPages = Math.ceil(invoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInvoices = invoices.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of table when page changes
    document.querySelector('.invoice-table-container')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Reset pagination when invoices change
  useEffect(() => {
    setCurrentPage(1);
  }, [invoices.length]);

  // State for upload review
  const [showUploadReview, setShowUploadReview] = useState(false);
  const [uploadId, setUploadId] = useState(null);
  const [isManualMode, setIsManualMode] = useState(false);

  // Reference data
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [hsCodes, setHsCodes] = useState([]);
  const [uomCodes, setUomCodes] = useState([]);

  // Search states
  const [buyerSearchLoading, setBuyerSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showBuyerModal, setShowBuyerModal] = useState(false);
  const [buyerSearchResults, setBuyerSearchResults] = useState([]);
  const [showBuyerDropdown, setShowBuyerDropdown] = useState(false);

  // Search Invoice states
  const [invoiceSearchLoading, setInvoiceSearchLoading] = useState(false);
  const [invoiceSearchResults, setInvoiceSearchResults] = useState([]);
  const [showInvoiceSearchDropdown, setShowInvoiceSearchDropdown] = useState(false);

  const [hsCodeSearchTerms, setHsCodeSearchTerms] = useState({});
  const [showHsCodeDropdowns, setShowHsCodeDropdowns] = useState({});
  const [filteredHsCodes, setFilteredHsCodes] = useState({});
  const [hsCodeSearchLoading, setHsCodeSearchLoading] = useState({});

  // JSON Response Modal
  const [jsonModal, setJsonModal] = useState({
    show: false,
    title: '',
    content: '',
    isDevelopmentMode: false
  });

  // State for invoice view modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // License Status
  const [licenseStatus, setLicenseStatus] = useState(null);

  // New invoice form state with ALL FBR required fields as per fbr.txt
  const [newInvoice, setNewInvoice] = useState({
    // Basic Invoice Information
    invoiceType: 'SALE_INVOICE',
    invoiceDate: new Date().toISOString().split('T')[0],

    // Seller Information (from user profile)
    sellerNTNCNIC: user?.ntn_cnic || '',
    sellerBusinessName: user?.business_name || '',
    sellerProvince: user?.province || '',
    sellerAddress: user?.address || '',

    // Buyer Information
    buyerNTNCNIC: '',
    buyerBusinessName: '',
    buyerProvince: '',
    buyerAddress: '',
    buyerRegistrationType: 'NTN',

    // Invoice Details
    invoiceRefNo: '',
    scenarioId: 'SN001',

    // Additional FBR Required Fields
    totalSaleValue: 0,
    totalTaxCharged: 0,
    totalAmountWithheld: 0,
    totalAmountPayable: 0,
    paymentMode: 'CASH',

    // Currency and Exchange Rate
    currencyCode: 'PKR',
    exchangeRate: 1,
    // Items array with ALL FBR required fields as per fbr.txt
    items: [{
      // Item Identification
      hsCode: '',
      productDescription: '',
      uoM: 'PCS',
      quantity: 1,

      // Pricing and Values
      rate: '18%', // Tax rate as string (for validation compatibility)
      totalValues: 0, // Total value including tax
      valueSalesExcludingST: '', // Value excluding sales tax - empty by default
      fixedNotifiedValueOrRetailPrice: 0,

      // Tax Information
      salesTaxApplicable: 18, // Sales tax amount - calculated from rate
      salesTaxRate: 18, // Sales tax rate percentage
      salesTaxWithheldAtSource: 0,
      extraTax: 0, // Should be number, not string
      furtherTax: 0,
      fedPayable: 0,

      // Additional Fields
      discount: 0,
      saleType: 1, // Should be number: 1=Goods, 2=Services
      sroScheduleNo: '',
      sroItemSerialNo: '',

      // Additional FBR fields from fbr.txt
      itemSerialNo: '',
      pcn: '', // Pakistan Customs Number
      invoiceItemSerialNo: 1
    }]
  });

  // Load license status
  const loadLicenseStatus = async () => {
    try {
      const response = await apiClient.get('/auth/license-status');
      setLicenseStatus(response.data);
    } catch (error) {
      console.error('Failed to load license status:', error);
      setLicenseStatus({
        hasLicense: false,
        hasFBRKeys: false,
        message: 'Unable to load license status'
      });
    }
  };

  useEffect(() => {
    loadInvoices();
    loadReferenceData();
    generateInvoiceReference();
    loadLicenseStatus();

    // Check if we're coming from upload
    const uploadIdParam = searchParams.get('uploadId');
    const modeParam = searchParams.get('mode');
    if (uploadIdParam) {
      setUploadId(uploadIdParam);
      setIsManualMode(modeParam === 'manual');
      setShowUploadReview(true);
    }
  }, [searchParams]);

  // Update form data when user data becomes available
  useEffect(() => {
    if (user) {
      setNewInvoice(prev => ({
        ...prev,
        sellerNTNCNIC: user.ntn_cnic || '',
        sellerBusinessName: user.business_name || '',
        sellerProvince: user.province || '',
        sellerAddress: user.address || ''
      }));
    }
  }, [user]);

  // Close HS code dropdowns when clicking outside or on window resize
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.hs-code-dropdown') && !event.target.closest('.hs-dropdown-menu')) {
        setShowHsCodeDropdowns({});
      }
    };

    const handleResize = () => {
      setShowHsCodeDropdowns({});
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, []);

  // Close invoice search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showInvoiceSearchDropdown && !event.target.closest('.invoice-search-container')) {
        setShowInvoiceSearchDropdown(false);
        setInvoiceSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showInvoiceSearchDropdown]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/invoice', {
        params: {
          page: 1,
          limit: 50,
          sortBy: 'created_at',
          sortOrder: 'DESC'
        }
      });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadReferenceData = async () => {
    try {
      const [provincesRes, hsCodesRes, uomRes] = await Promise.all([
        apiClient.get('/reference/provinces'),
        apiClient.get('/reference/hs-codes?limit=1000'),
        apiClient.get('/reference/uom?limit=1000')
      ]);

      setProvinces(provincesRes.data.data || []);
      setHsCodes(hsCodesRes.data.data || []);
      setUomCodes(uomRes.data.data || []);
      setInvoiceTypes([
        { code: 'SALE_INVOICE', description: 'Sale Invoice' },
        { code: 'PURCHASE_INVOICE', description: 'Purchase Invoice' },
        { code: 'CREDIT_NOTE', description: 'Credit Note' },
        { code: 'DEBIT_NOTE', description: 'Debit Note' }
      ]);
    } catch (error) {
      console.error('Failed to load reference data:', error);
      toast.error('Failed to load reference data');
    }
  };

  // Generate invoice reference number
  const generateInvoiceReference = async () => {
    try {
      const response = await apiClient.get('/invoice/generate-reference');
      setNewInvoice(prev => ({
        ...prev,
        invoiceRefNo: response.data.reference_number
      }));
    } catch (error) {
      console.error('Failed to generate invoice reference:', error);
      // Generate a fallback reference number
      const timestamp = Date.now();
      const fallbackRef = `INV-${timestamp}`;
      setNewInvoice(prev => ({
        ...prev,
        invoiceRefNo: fallbackRef
      }));
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);

      // Build search parameters
      const searchParams = {
        page: 1,
        limit: 50,
        sortBy: 'created_at',
        sortOrder: 'DESC'
      };

      // Check if any search criteria is provided
      const hasSearchCriteria =
        searchFilters.buyerRegistrationNo?.trim() ||
        searchFilters.buyerName?.trim() ||
        searchFilters.invoiceNo?.trim() ||
        searchFilters.invoiceStatus ||
        searchFilters.invoiceType ||
        searchFilters.fromDate ||
        searchFilters.toDate ||
        searchFilters.transactionType !== 'ALL';

      // Add filters if they have values
      if (searchFilters.buyerRegistrationNo?.trim()) {
        searchParams.buyerNTNCNIC = searchFilters.buyerRegistrationNo.trim();
      }
      if (searchFilters.buyerName?.trim()) {
        searchParams.buyerBusinessName = searchFilters.buyerName.trim();
      }
      if (searchFilters.invoiceNo?.trim()) {
        searchParams.invoiceRefNo = searchFilters.invoiceNo.trim();
      }
      if (searchFilters.invoiceStatus) {
        searchParams.status = searchFilters.invoiceStatus;
      }
      if (searchFilters.invoiceType) {
        searchParams.invoiceType = searchFilters.invoiceType;
      }
      if (searchFilters.fromDate) {
        searchParams.fromDate = searchFilters.fromDate;
      }
      if (searchFilters.toDate) {
        searchParams.toDate = searchFilters.toDate;
      }

      // Filter by transaction type
      if (searchFilters.transactionType === 'SALES') {
        searchParams.invoiceType = 'SALE_INVOICE';
      } else if (searchFilters.transactionType === 'PURCHASES') {
        searchParams.invoiceType = 'PURCHASE_INVOICE';
      }

      console.log('Search parameters:', searchParams);

      const response = await apiClient.get('/invoice', { params: searchParams });
      const invoicesData = response.data.invoices || [];
      setInvoices(invoicesData);

      // Provide better user feedback
      if (invoicesData.length === 0) {
        if (hasSearchCriteria) {
          toast.info('No invoices found matching your search criteria');
        } else {
          toast.info('No invoices found. Try adding some search criteria or check if you have any invoices.');
        }
      } else {
        const searchMessage = hasSearchCriteria
          ? `Found ${invoicesData.length} invoice${invoicesData.length === 1 ? '' : 's'} matching your criteria`
          : `Loaded ${invoicesData.length} recent invoice${invoicesData.length === 1 ? '' : 's'}`;
        toast.success(searchMessage);
      }
    } catch (error) {
      console.error('Failed to search invoices:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to search invoices';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchFilters({
      buyerRegistrationNo: '',
      buyerName: '',
      invoiceNo: '',
      sellerRegistrationNo: '',
      sellerName: '',
      invoiceStatus: '',
      invoiceType: '',
      saleType: '',
      itemSerialNo: '',
      fromDate: '',
      toDate: '',
      transactionType: 'ALL'
    });
    loadInvoices(); // Reload all invoices
  };

  const handleFilterChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));

    // Trigger auto-search for buyer fields
    if (field === 'buyerRegistrationNo' && value.length >= 3) {
      searchInvoiceCustomers(value, 'ntn_cnic');
    } else if (field === 'buyerName' && value.length >= 3) {
      searchInvoiceCustomers(value, 'business_name');
    } else if ((field === 'buyerRegistrationNo' || field === 'buyerName') && value.length < 3) {
      setShowInvoiceSearchDropdown(false);
      setInvoiceSearchResults([]);
    }
  };

  const handleNewInvoiceChange = (field, value) => {
    setNewInvoice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Buyer search functionality
  const handleBuyerSearch = async (searchTerm, searchType = 'ntn_cnic') => {
    if (!searchTerm || searchTerm.length < 3) return;

    console.log('🔍 Searching buyers (handleBuyerSearch):', { searchTerm, searchType });
    setBuyerSearchLoading(true);
    try {
      const response = await apiClient.get('/customers', {
        params: {
          search: searchTerm,
          limit: 10
        }
      });

      console.log('📊 Buyer search response (handleBuyerSearch):', response.data);
      const foundCustomers = response.data.data || [];
      console.log('👥 Found buyers (handleBuyerSearch):', foundCustomers.length, foundCustomers);
      if (foundCustomers.length > 0) {
        // Find the best match based on search type
        let customer = foundCustomers[0];

        if (searchType === 'ntn_cnic') {
          const exactMatch = foundCustomers.find(c =>
            c.ntn_cnic && c.ntn_cnic.includes(searchTerm)
          );
          if (exactMatch) customer = exactMatch;
        } else if (searchType === 'business_name') {
          const exactMatch = foundCustomers.find(c =>
            c.business_name && c.business_name.toLowerCase().includes(searchTerm.toLowerCase())
          );
          if (exactMatch) customer = exactMatch;
        }

        setNewInvoice(prev => ({
          ...prev,
          buyerNTNCNIC: customer.ntn_cnic || '',
          buyerBusinessName: customer.business_name || '',
          buyerProvince: customer.province || '',
          buyerAddress: customer.address || '',
          buyerRegistrationType: customer.registration_type || 'NTN',
          scenarioId: 'SN001' // Reset to SN001 for registered buyers
        }));
        setSelectedCustomer(customer);
        toast.success(`Customer found: ${customer.business_name}`);
      } else {
        toast('No matching customers found', {
          icon: '🔍',
          duration: 3000,
          style: {
            background: '#F3F4F6',
            color: '#374151'
          }
        });
      }
    } catch (error) {
      console.error('Failed to search buyer:', error);
      // Don't show error toast for search failures as it's not critical
    } finally {
      setBuyerSearchLoading(false);
    }
  };

  // Search buyers for modal (similar to InvoiceForm)
  const searchBuyers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setBuyerSearchResults([]);
      setShowBuyerDropdown(false);
      return;
    }

    setBuyerSearchLoading(true);
    try {
      const response = await apiClient.get('/customers', {
        params: {
          search: searchTerm,
          limit: 10
        }
      });

      const foundCustomers = response.data.data || [];
      setBuyerSearchResults(foundCustomers);
      setShowBuyerDropdown(foundCustomers.length > 0);
    } catch (error) {
      console.error('Error searching buyers:', error);
      setBuyerSearchResults([]);
      setShowBuyerDropdown(false);
    } finally {
      setBuyerSearchLoading(false);
    }
  };

  // Select buyer from dropdown
  const selectBuyerFromDropdown = (customer) => {
    setNewInvoice(prev => ({
      ...prev,
      buyerNTNCNIC: customer.ntn_cnic || '',
      buyerBusinessName: customer.business_name || '',
      buyerProvince: customer.province || '',
      buyerAddress: customer.address || '',
      buyerRegistrationType: customer.registration_type || 'NTN',
      scenarioId: 'SN001' // Reset to SN001 for registered buyers
    }));
    setSelectedCustomer(customer);
    setShowBuyerDropdown(false);
    setBuyerSearchResults([]);
    setShowBuyerModal(false);
    toast.success(`Customer selected: ${customer.business_name}`);
  };

  // Search customers for invoice search filters
  const searchInvoiceCustomers = async (searchTerm, searchType) => {
    if (!searchTerm || searchTerm.length < 2) {
      setInvoiceSearchResults([]);
      setShowInvoiceSearchDropdown(false);
      return;
    }

    console.log('🔍 Searching invoice customers:', { searchTerm, searchType });
    setInvoiceSearchLoading(true);
    try {
      const response = await apiClient.get('/customers', {
        params: {
          search: searchTerm,
          limit: 10
        }
      });

      console.log('📊 Invoice customer search response:', response.data);
      const foundCustomers = response.data.data || [];
      console.log('👥 Found customers:', foundCustomers.length, foundCustomers);

      setInvoiceSearchResults(foundCustomers);
      setShowInvoiceSearchDropdown(foundCustomers.length > 0);

      if (foundCustomers.length === 0) {
        toast('No customers found matching your search', {
          icon: '🔍',
          duration: 3000,
          style: {
            background: '#F3F4F6',
            color: '#374151'
          }
        });
      }
    } catch (error) {
      console.error('Error searching invoice customers:', error);
      toast.error('Failed to search customers');
      setInvoiceSearchResults([]);
      setShowInvoiceSearchDropdown(false);
    } finally {
      setInvoiceSearchLoading(false);
    }
  };

  // Select customer from invoice search dropdown
  const selectInvoiceCustomer = (customer, fieldType) => {
    if (fieldType === 'buyerRegistrationNo') {
      setSearchFilters(prev => ({
        ...prev,
        buyerRegistrationNo: customer.ntn_cnic || '',
        buyerName: customer.business_name || ''
      }));
    } else if (fieldType === 'buyerName') {
      setSearchFilters(prev => ({
        ...prev,
        buyerName: customer.business_name || '',
        buyerRegistrationNo: customer.ntn_cnic || ''
      }));
    }

    setShowInvoiceSearchDropdown(false);
    setInvoiceSearchResults([]);
    toast.success(`Customer selected: ${customer.business_name}`);
  };

  // Handle buyer field changes with auto-search
  const handleBuyerFieldChange = (field, value) => {
    // If a customer is selected and user tries to modify the business name, prevent it
    if (selectedCustomer && field === 'buyerBusinessName' && value !== selectedCustomer.business_name) {
      toast('Customer is already selected. Click the X button to clear selection and edit manually.', {
        icon: '🔒',
        duration: 4000,
        style: {
          background: '#FEF3C7',
          color: '#92400E',
          border: '1px solid #F59E0B'
        }
      });
      return;
    }

    // Clear selected customer when user starts typing (except for business name when customer is selected)
    if (selectedCustomer && field !== 'buyerBusinessName') {
      setSelectedCustomer(null);
    }

    setNewInvoice(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-search when typing NTN/CNIC or business name
    if (field === 'buyerNTNCNIC' && value.length >= 5) {
      handleBuyerSearch(value, 'ntn_cnic');
    } else if (field === 'buyerBusinessName' && value.length >= 3) {
      handleBuyerSearch(value, 'business_name');
    }
  };

  // Handle Walk-in Customer selection
  const handleWalkInCustomer = () => {
    setSelectedCustomer(null); // Clear any selected customer
    setNewInvoice(prev => ({
      ...prev,
      buyerNTNCNIC: '9999999',
      buyerBusinessName: 'Walk-in Customer',
      buyerAddress: 'N/A',
      buyerProvince: 'PB', // Punjab province code
      buyerRegistrationType: 'NTN',
      scenarioId: 'SN002' // Change to SN002 for unregistered buyers (Walk-in Customer)
    }));
    toast.success('Walk-in Customer details added');
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...newInvoice.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    // Auto-populate product description when HS code is selected
    if (field === 'hsCode' && value) {
      const selectedHsCode = hsCodes.find(hs => hs.code === value);
      if (selectedHsCode) {
        updatedItems[index].productDescription = selectedHsCode.description || '';
      }
    }

    // Calculate amounts based on FBR requirements - PERCENTAGE CALCULATION
    if (field === 'quantity' || field === 'valueSalesExcludingST' || field === 'salesTaxApplicable') {
      const quantity = parseFloat(updatedItems[index].quantity) || 1;
      const valueSalesExcludingST = parseFloat(updatedItems[index].valueSalesExcludingST) || 0;
      const salesTaxRate = parseFloat(updatedItems[index].salesTaxApplicable) || 0; // This is the percentage

      // Calculate total values: Base Amount + (Base Amount * Tax Rate %)
      const baseAmount = valueSalesExcludingST * quantity;
      const salesTaxAmount = (baseAmount * salesTaxRate) / 100;
      const totalValue = baseAmount + salesTaxAmount;

      // Ensure totalValues is always a number
      updatedItems[index].totalValues = isNaN(totalValue) ? 0 : parseFloat(totalValue.toFixed(2));
    }

    // Ensure all items have valid totalValues
    updatedItems.forEach((item, idx) => {
      if (typeof item.totalValues !== 'number' || isNaN(item.totalValues)) {
        updatedItems[idx].totalValues = 0;
      }
    });

    setNewInvoice(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  // Debounced HS Code search functionality with server-side search
  const handleHsCodeSearch = (index, searchTerm) => {
    setHsCodeSearchTerms(prev => ({
      ...prev,
      [index]: searchTerm
    }));

    // Also update the actual form data with the typed HS Code
    handleItemChange(index, 'hsCode', searchTerm);

    if (searchTerm.length >= 2) {
      // Clear previous timeout
      if (window.hsCodeSearchTimeout) {
        clearTimeout(window.hsCodeSearchTimeout);
      }

      // Set loading state
      setHsCodeSearchLoading(prev => ({
        ...prev,
        [index]: true
      }));

      // Debounce the search
      window.hsCodeSearchTimeout = setTimeout(async () => {
        try {
          // Search via API for comprehensive results
          const response = await apiClient.get(`/reference/hs-codes?search=${encodeURIComponent(searchTerm)}&limit=100`);
          const searchResults = response.data.data || [];

          console.log(`🔍 HS Code search for "${searchTerm}":`, searchResults.length, 'results found');

          // Log specific search for debugging
          if (searchTerm.includes('6001') || searchTerm.toLowerCase().includes('pile')) {
            console.log('🎯 Specific search results:', searchResults.slice(0, 5));
          }

          setFilteredHsCodes(prev => ({
            ...prev,
            [index]: searchResults
          }));

          setShowHsCodeDropdowns(prev => ({
            ...prev,
            [index]: true
          }));
        } catch (error) {
          console.error('Failed to search HS codes:', error);
          // Fallback to client-side search if API fails
          const filtered = hsCodes.filter(hs =>
            hs.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
            hs.description.toLowerCase().includes(searchTerm.toLowerCase())
          ).slice(0, 50);

          console.log(`🔄 Fallback search for "${searchTerm}":`, filtered.length, 'results found');

          setFilteredHsCodes(prev => ({
            ...prev,
            [index]: filtered
          }));

          setShowHsCodeDropdowns(prev => ({
            ...prev,
            [index]: true
          }));
        } finally {
          setHsCodeSearchLoading(prev => ({
            ...prev,
            [index]: false
          }));
        }
      }, 300); // 300ms debounce
    } else {
      setShowHsCodeDropdowns(prev => ({
        ...prev,
        [index]: false
      }));
    }
  };

  const selectHsCode = (index, hsCode) => {
    // Update both HS Code and Product Description
    handleItemChange(index, 'hsCode', hsCode.code);
    handleItemChange(index, 'productDescription', hsCode.description);

    setHsCodeSearchTerms(prev => ({
      ...prev,
      [index]: hsCode.code
    }));
    setShowHsCodeDropdowns(prev => ({
      ...prev,
      [index]: false
    }));
  };

  const addNewItem = () => {
    setNewInvoice(prev => ({
      ...prev,
      items: [...prev.items, {
        hsCode: '',
        productDescription: '',
        rate: '18%',
        uoM: 'PCS',
        quantity: 1,
        totalValues: 0,
        valueSalesExcludingST: 0,
        fixedNotifiedValueOrRetailPrice: 0,
        salesTaxApplicable: 18,
        salesTaxWithheldAtSource: 0,
        extraTax: '',
        furtherTax: 0,
        sroScheduleNo: '',
        fedPayable: 0,
        discount: 0,
        saleType: 'Goods at standard rate (default)',
        sroItemSerialNo: ''
      }]
    }));
  };

  const removeItem = (index) => {
    if (newInvoice.items.length > 1) {
      setNewInvoice(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSaveInvoice = async () => {
    try {
      // Validate required fields
      if (!newInvoice.buyerNTNCNIC) {
        toast.error('Buyer NTN/CNIC is required');
        return;
      }
      if (!newInvoice.buyerBusinessName) {
        toast.error('Buyer Business Name is required');
        return;
      }
      if (!newInvoice.buyerProvince) {
        toast.error('Buyer Province is required');
        return;
      }


      // Validate items
      for (let i = 0; i < newInvoice.items.length; i++) {
        const item = newInvoice.items[i];

        // Debug logging to help identify validation issues
        console.log(`Validating item ${i + 1}:`, {
          hsCode: item.hsCode,
          productDescription: item.productDescription,
          uoM: item.uoM,
          quantity: item.quantity,
          totalValues: item.totalValues,
          hsCodeSearchTerm: hsCodeSearchTerms[i]
        });

        if (!item.hsCode || item.hsCode.trim() === '') {
          toast.error(`HS Code is required for item ${i + 1}`);
          return;
        }
        if (!item.productDescription || item.productDescription.trim() === '') {
          toast.error(`Product Description is required for item ${i + 1}`);
          return;
        }
        if (!item.uoM || item.uoM.trim() === '') {
          toast.error(`Unit of Measure is required for item ${i + 1}`);
          return;
        }
        if (!item.quantity || item.quantity <= 0) {
          toast.error(`Valid quantity is required for item ${i + 1}`);
          return;
        }
        if (!item.totalValues || item.totalValues <= 0) {
          toast.error(`Total value must be greater than 0 for item ${i + 1}`);
          return;
        }
      }

      const invoiceData = {
        ...newInvoice,
        // Ensure all required fields are present
        sellerNTNCNIC: newInvoice.sellerNTNCNIC || user?.ntn_cnic || '',
        sellerBusinessName: newInvoice.sellerBusinessName || user?.business_name || '',
        sellerProvince: newInvoice.sellerProvince || user?.province || '',
        sellerAddress: newInvoice.sellerAddress || user?.address || '',
        buyerAddress: newInvoice.buyerAddress || '',
        items: newInvoice.items.map(item => ({
          ...item,
          // Ensure numeric fields are properly formatted
          quantity: parseFloat(item.quantity) || 1,
          totalValues: parseFloat(item.totalValues) || 0,
          valueSalesExcludingST: parseFloat(item.valueSalesExcludingST) || 0,
          salesTaxApplicable: parseFloat(item.salesTaxApplicable) || 0,
          salesTaxWithheldAtSource: parseFloat(item.salesTaxWithheldAtSource) || 0,
          furtherTax: parseFloat(item.furtherTax) || 0,
          fedPayable: parseFloat(item.fedPayable) || 0,
          discount: parseFloat(item.discount) || 0,
          fixedNotifiedValueOrRetailPrice: parseFloat(item.fixedNotifiedValueOrRetailPrice) || 0,
          // Ensure string fields are not empty
          extraTax: item.extraTax || '',
          sroScheduleNo: item.sroScheduleNo || '',
          sroItemSerialNo: item.sroItemSerialNo || '',
          saleType: item.saleType || 'Goods at standard rate (default)'
        }))
      };

      console.log('Sending invoice data:', invoiceData);

      await apiClient.post('/invoice', invoiceData);
      toast.success('Invoice created successfully');
      setShowNewInvoiceForm(false);
      loadInvoices();
      generateInvoiceReference(); // Generate new reference for next invoice

      // Reset form
      setNewInvoice({
        invoiceType: 'SALE_INVOICE',
        invoiceDate: new Date().toISOString().split('T')[0],
        sellerNTNCNIC: user?.ntn_cnic || '',
        sellerBusinessName: user?.business_name || '',
        sellerProvince: user?.province || '',
        sellerAddress: user?.address || '',
        buyerNTNCNIC: '',
        buyerBusinessName: '',
        buyerProvince: '',
        buyerAddress: '',
        buyerRegistrationType: 'NTN',
        invoiceRefNo: '',
        scenarioId: 'SN001',
        items: [{
          hsCode: '',
          productDescription: '',
          rate: '18%',
          uoM: 'PCS',
          quantity: 1,
          totalValues: 0,
          valueSalesExcludingST: 0,
          fixedNotifiedValueOrRetailPrice: 0,
          salesTaxApplicable: 18,
          salesTaxWithheldAtSource: 0,
          extraTax: '',
          furtherTax: 0,
          sroScheduleNo: '',
          fedPayable: 0,
          discount: 0,
          saleType: 'Goods at standard rate (default)',
          sroItemSerialNo: ''
        }]
      });
    } catch (error) {
      console.error('Failed to create invoice:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to create invoice';
      toast.error(errorMessage);
    }
  };

  // FBR Validation
  const handleFBRValidate = async (invoiceId) => {
    const loadingKey = `validate_${invoiceId}`;
    setActionLoading(prev => ({ ...prev, [loadingKey]: true }));

    // Show loading toast
    toast.loading('Validating invoice with FBR API...', {
      id: `validate-${invoiceId}`,
      duration: 15000 // Show for 15 seconds max
    });

    try {
      const response = await apiClient.post(`/invoice/${invoiceId}/validate`);

      // Show detailed response in a modal
      const responseData = response.data;
      const isDevelopmentMode = responseData.isDevelopmentMode;

      // Create a formatted JSON display with error handling
      let jsonResponse;
      try {
        const responseToDisplay = responseData.apiResponse || responseData.validation_result;
        jsonResponse = JSON.stringify(responseToDisplay, null, 2);
      } catch (error) {
        console.error('Failed to stringify response:', error);
        jsonResponse = JSON.stringify({
          error: 'Failed to format response',
          rawResponse: responseData,
          message: 'The FBR API response could not be properly formatted for display'
        }, null, 2);
      }

      // Show the JSON modal
      showJSONModal('FBR Validation Response', jsonResponse, isDevelopmentMode);

      // Dismiss loading toast and show success message
      toast.dismiss(`validate-${invoiceId}`);
      if (isDevelopmentMode) {
        toast.success('Invoice validated successfully (Development Mode) - Check the API response popup for details');
      } else {
        toast.success('Invoice validated successfully - Check the API response popup for details');
      }

      loadInvoices(); // Refresh the list
    } catch (error) {
      console.error('Failed to validate with FBR:', error);

      // Dismiss loading toast and show error
      toast.dismiss(`validate-${invoiceId}`);
      const errorMessage = error.response?.data?.error || 'Failed to validate with FBR';
      toast.error(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  // FBR Submission
  const handleFBRSubmit = async (invoiceId) => {
    const loadingKey = `submit_${invoiceId}`;
    setActionLoading(prev => ({ ...prev, [loadingKey]: true }));

    // Show loading toast
    toast.loading('Submitting invoice to FBR API...', {
      id: `submit-${invoiceId}`,
      duration: 15000 // Show for 15 seconds max
    });

    try {
      const response = await apiClient.post(`/invoice/${invoiceId}/submit`);

      // Show detailed response in a modal
      const responseData = response.data;
      const isDevelopmentMode = responseData.isDevelopmentMode;

      // Create a formatted JSON display with error handling
      let jsonResponse;
      try {
        const responseToDisplay = responseData.apiResponse || responseData.submission_result;
        jsonResponse = JSON.stringify(responseToDisplay, null, 2);
      } catch (error) {
        console.error('Failed to stringify response:', error);
        jsonResponse = JSON.stringify({
          error: 'Failed to format response',
          rawResponse: responseData,
          message: 'The FBR API response could not be properly formatted for display'
        }, null, 2);
      }

      // Show the JSON modal
      showJSONModal('FBR Submission Response', jsonResponse, isDevelopmentMode);

      // Dismiss loading toast and show success message
      toast.dismiss(`submit-${invoiceId}`);
      if (isDevelopmentMode) {
        toast.success('Invoice submitted successfully (Development Mode) - Check the API response popup for details');
      } else {
        toast.success('Invoice submitted to FBR successfully - Check the API response popup for details');
      }

      loadInvoices(); // Refresh the list
    } catch (error) {
      console.error('Failed to submit to FBR:', error);

      // Dismiss loading toast and show error
      toast.dismiss(`submit-${invoiceId}`);
      const errorMessage = error.response?.data?.error || 'Failed to submit to FBR';
      toast.error(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Show JSON response modal
  const showJSONModal = (title, content, isDevelopmentMode = false) => {
    setJsonModal({
      show: true,
      title,
      content,
      isDevelopmentMode
    });
  };

  // Handle view invoice
  const handleViewInvoice = async (invoice) => {
    try {
      console.log('🔍 VIEW CLICKED - Invoice ID:', invoice.id, 'Invoice Number:', invoice.invoice_number);
      console.log('🔍 Full invoice object:', invoice);

      // Clear previous invoice first to force re-render
      setSelectedInvoice(null);
      setShowViewModal(false);

      console.log('📡 Making API call to load invoice:', invoice.id);
      const response = await apiClient.get(`/invoice/${invoice.id}`);
      console.log('✅ API Response received for invoice:', response.data.invoice.id, response.data.invoice.invoice_number);

      setSelectedInvoice(response.data.invoice);
      setShowViewModal(true);
      console.log('🎯 Modal should now show invoice:', response.data.invoice.id);
    } catch (error) {
      console.error('❌ Failed to load invoice details:', error);
      toast.error('Failed to load invoice details');
    }
  };

  // Helper functions for invoice actions
  const canValidateInvoice = (invoice) => {
    return invoice.status === 'draft' || invoice.status === 'pending';
  };

  const canSubmitInvoice = (invoice) => {
    return invoice.status === 'validated' || invoice.status === 'draft';
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'draft': 'bg-gray-100 text-gray-800',
      'submitted': 'bg-blue-100 text-blue-800',
      'validated': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || statusColors.draft}`}>
        {status || 'Draft'}
      </span>
    );
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          .hs-dropdown-container {
            position: relative !important;
            z-index: 1000 !important;
          }
          .hs-code-dropdown {
            position: relative !important;
          }
          .hs-dropdown-menu {
            position: absolute !important;
            z-index: 9999 !important;
            background: white !important;
            border: 1px solid #d1d5db !important;
            border-radius: 6px !important;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
            max-height: 300px !important;
          }
          /* Force native select styling to ensure visibility */
          select {
            background-color: #ffffff !important;
            color: #1f2937 !important;
            border: 1px solid #d1d5db !important;
            border-radius: 0.375rem;
            padding: 0.5rem 0.75rem;
            font-size: 0.875rem;
            line-height: 1.25rem;
            /* Use native appearance for better compatibility */
            -webkit-appearance: menulist !important;
            -moz-appearance: menulist !important;
            appearance: menulist !important;
          }
          select:focus {
            outline: 2px solid transparent;
            outline-offset: 2px;
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }
          /* Force option styling with maximum specificity */
          select option {
            background-color: #ffffff !important;
            color: #1f2937 !important;
            padding: 8px 12px !important;
            font-size: 0.875rem !important;
            line-height: 1.25rem !important;
          }
          select option:hover {
            background-color: #f3f4f6 !important;
            color: #1f2937 !important;
          }
          select option:checked, select option:focus {
            background-color: #3b82f6 !important;
            color: #ffffff !important;
          }
          select option[value=""] {
            color: #6b7280 !important;
            font-style: italic;
          }
          /* Specific styling for province dropdowns */
          .destination-select, .buyer-province-select {
            background-color: #ffffff !important;
            color: #1f2937 !important;
            border: 1px solid #d1d5db !important;
            -webkit-appearance: menulist !important;
            -moz-appearance: menulist !important;
            appearance: menulist !important;
          }
          .destination-select option, .buyer-province-select option {
            background-color: #ffffff !important;
            color: #1f2937 !important;
            padding: 8px 12px !important;
          }
          .destination-select option[value=""], .buyer-province-select option[value=""] {
            color: #6b7280 !important;
            background-color: #ffffff !important;
          }
          .table-container {
            overflow-x: auto !important;
            overflow-y: visible !important;
            position: relative !important;
            min-height: 400px !important;
          }
          .invoice-items-section {
            min-height: 500px !important;
            position: relative !important;
          }
          .hs-search-input {
            position: relative !important;
          }
          .hs-search-input::after {
            content: '🔍';
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
            pointer-events: none;
            color: #6b7280;
          }
        `
      }} />
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg mb-6">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center">
                <DocumentTextIcon className="h-8 w-8 mr-3" />
                Sales Tax Invoice Management
              </h1>
              <p className="text-blue-100 mt-1">Domestic Transactions</p>
            </div>

          </div>
        </div>
      </div>

      {/* License Status */}
      {licenseStatus && (
        <div className={`rounded-lg shadow-lg mb-6 ${
          licenseStatus.canSubmitInvoices
            ? 'bg-green-50 border border-green-200'
            : 'bg-yellow-50 border border-yellow-200'
        }`}>
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`h-3 w-3 rounded-full mr-3 ${
                  licenseStatus.canSubmitInvoices ? 'bg-green-400' : 'bg-yellow-400'
                }`}></div>
                <div>
                  <h3 className={`text-sm font-medium ${
                    licenseStatus.canSubmitInvoices ? 'text-green-800' : 'text-yellow-800'
                  }`}>
                    {licenseStatus.canSubmitInvoices ? 'Ready for FBR Integration' : 'Setup Required'}
                  </h3>
                  <p className={`text-xs mt-1 ${
                    licenseStatus.canSubmitInvoices ? 'text-green-600' : 'text-yellow-600'
                  }`}>
                    {licenseStatus.message}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4 text-xs">
                <div className={`px-2 py-1 rounded ${
                  licenseStatus.hasLicense && !licenseStatus.isLicenseExpired
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  License: {licenseStatus.hasLicense && !licenseStatus.isLicenseExpired ? 'Active' : 'Required'}
                </div>
                <div className={`px-2 py-1 rounded ${
                  licenseStatus.hasFBRKeys ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  FBR Keys: {licenseStatus.hasFBRKeys ? 'Configured' : 'Missing'}
                </div>
                {licenseStatus.license && (
                  <div className="text-gray-600">
                    Usage: {licenseStatus.license.current_usage}/{licenseStatus.license.monthly_limit}
                  </div>
                )}
              </div>
            </div>
            {!licenseStatus.canSubmitInvoices && (
              <div className="mt-3 text-xs text-yellow-700">
                <p>To submit invoices to FBR, you need:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {!licenseStatus.hasLicense && <li>Active API Key License (contact administrator)</li>}
                  {licenseStatus.isLicenseExpired && <li>License renewal (contact administrator)</li>}
                  {!licenseStatus.hasFBRKeys && (
                    <li>
                      FBR API keys configured in{' '}
                      <button
                        onClick={() => navigate('/app/profile?tab=fbr-api')}
                        className="underline hover:text-yellow-800"
                      >
                        Profile Settings
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
            Search Invoices
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="relative invoice-search-container">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buyer Registration No / CNIC
                {invoiceSearchLoading && (
                  <span className="ml-2 text-xs text-blue-600">Searching...</span>
                )}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchFilters.buyerRegistrationNo}
                  onChange={(e) => handleFilterChange('buyerRegistrationNo', e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter registration no (auto-search)"
                />
                <MagnifyingGlassIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            <div className="relative invoice-search-container">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buyer Name
                {invoiceSearchLoading && (
                  <span className="ml-2 text-xs text-blue-600">Searching...</span>
                )}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchFilters.buyerName}
                  onChange={(e) => handleFilterChange('buyerName', e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter buyer name (auto-search)"
                />
                <MagnifyingGlassIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice No
              </label>
              <input
                type="text"
                value={searchFilters.invoiceNo}
                onChange={(e) => handleFilterChange('invoiceNo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter invoice no"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Status
              </label>
              <select
                value={searchFilters.invoiceStatus}
                onChange={(e) => handleFilterChange('invoiceStatus', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="validated">Validated</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transaction Type
              </label>
              <select
                value={searchFilters.transactionType}
                onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ALL">All Transactions</option>
                <option value="SALES">Domestic Sales</option>
                <option value="PURCHASES">Domestic Purchases</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sale Type
              </label>
              <select
                value={searchFilters.saleType}
                onChange={(e) => handleFilterChange('saleType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select</option>
                <option value="Goods at standard rate (default)">Goods at standard rate</option>
                <option value="Goods at reduced rate">Goods at reduced rate</option>
                <option value="Goods at zero rate">Goods at zero rate</option>
              </select>
            </div>



            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={searchFilters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={searchFilters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={handleClear}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200"
            >
              Clear
            </button>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center"
            >
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              Search
            </button>
          </div>
        </div>

        {/* Invoice Search Dropdown */}
        {showInvoiceSearchDropdown && invoiceSearchResults.length > 0 && (
          <div className="relative mt-4 invoice-search-container">
            <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
              <div className="p-2 text-xs text-gray-500 border-b bg-gray-50">
                Found {invoiceSearchResults.length} customer(s). Click to select:
              </div>
              {invoiceSearchResults.map((customer) => (
                <div
                  key={customer.id}
                  onClick={() => selectInvoiceCustomer(customer, 'buyerName')}
                  className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{customer.business_name}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {customer.ntn_cnic && (
                          <span className="inline-block mr-4">NTN/CNIC: {customer.ntn_cnic}</span>
                        )}
                        {customer.province && (
                          <span className="inline-block">Province: {customer.province}</span>
                        )}
                      </div>
                      {customer.address && (
                        <div className="text-xs text-gray-500 mt-1">{customer.address}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* New Invoice Form */}
      {showNewInvoiceForm && (
        <div className="bg-white rounded-lg shadow-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <PlusIcon className="h-5 w-5 mr-2" />
              Add New Invoice
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Type *
                </label>
                <select
                  value={newInvoice.invoiceType}
                  onChange={(e) => handleNewInvoiceChange('invoiceType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  {invoiceTypes.map((type) => (
                    <option key={type.code} value={type.code}>
                      {type.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Date *
                </label>
                <input
                  type="date"
                  value={newInvoice.invoiceDate}
                  onChange={(e) => handleNewInvoiceChange('invoiceDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sale Type *
                </label>
                <select
                  value={newInvoice.saleType}
                  onChange={(e) => handleNewInvoiceChange('saleType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="Goods at standard rate (default)">Goods at standard rate</option>
                  <option value="Goods at reduced rate">Goods at reduced rate</option>
                  <option value="Goods at zero rate">Goods at zero rate</option>
                </select>
              </div>
            </div>

            {/* Buyer Information Section */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Buyer Information</h3>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleWalkInCustomer}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <UserIcon className="h-4 w-4" />
                  Walk-in Customer
                </button>
                <button
                  type="button"
                  onClick={() => setShowBuyerModal(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  Search Buyers
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buyer NTN/CNIC *
                  {buyerSearchLoading && (
                    <span className="ml-2 text-xs text-blue-600">Searching...</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={newInvoice.buyerNTNCNIC}
                    onChange={(e) => handleBuyerFieldChange('buyerNTNCNIC', e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter buyer NTN/CNIC (auto-search)"
                    required
                  />
                  <MagnifyingGlassIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buyer Business Name *
                  {buyerSearchLoading && (
                    <span className="ml-2 text-xs text-blue-600">Searching...</span>
                  )}
                  {selectedCustomer && (
                    <span className="ml-2 text-xs text-green-600">✓ Customer Selected</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={newInvoice.buyerBusinessName}
                    onChange={(e) => handleBuyerFieldChange('buyerBusinessName', e.target.value)}
                    className={`w-full px-3 py-2 pr-20 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      selectedCustomer
                        ? 'border-green-300 bg-green-50 cursor-not-allowed'
                        : 'border-gray-300'
                    }`}
                    placeholder={selectedCustomer ? "Customer selected - click X to edit" : "Enter buyer business name (auto-search)"}
                    title={selectedCustomer ? "Customer is selected. Click the X button to clear selection and edit manually." : ""}
                    required
                  />
                  {selectedCustomer ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCustomer(null);
                        setNewInvoice(prev => ({
                          ...prev,
                          buyerBusinessName: '',
                          buyerNTNCNIC: '',
                          buyerAddress: '',
                          buyerProvince: '',
                          buyerRegistrationType: 'NTN',
                          scenarioId: 'SN001' // Reset to SN001 when clearing customer
                        }));
                      }}
                      className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 hover:text-gray-600"
                      title="Clear customer selection"
                    >
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <MagnifyingGlassIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                  )}
                </div>

                {/* Customer selection indicator */}
                {selectedCustomer && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center text-sm text-green-700">
                      <svg className="h-4 w-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">Customer selected:</span>
                      <span className="ml-1">{selectedCustomer.business_name}</span>
                    </div>
                    <div className="text-xs text-green-600 mt-1">
                      All buyer information has been auto-filled. Click the X button above to edit manually.
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buyer Province *
                </label>
                <CustomSelect
                  value={newInvoice.buyerProvince}
                  onChange={(value) => handleNewInvoiceChange('buyerProvince', value)}
                  options={provinces}
                  placeholder="Select Province"
                  className="w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buyer Address
                </label>
                <input
                  type="text"
                  value={newInvoice.buyerAddress}
                  onChange={(e) => handleNewInvoiceChange('buyerAddress', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter buyer address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buyer Registration Type
                </label>
                <select
                  value={newInvoice.buyerRegistrationType}
                  onChange={(e) => handleNewInvoiceChange('buyerRegistrationType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="NTN">NTN</option>
                  <option value="CNIC">CNIC</option>
                  <option value="PASSPORT">Passport</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Reference No
                </label>
                <input
                  type="text"
                  value={newInvoice.invoiceRefNo}
                  onChange={(e) => handleNewInvoiceChange('invoiceRefNo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Auto-generated"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scenario ID
                </label>
                <select
                  value={newInvoice.scenarioId}
                  onChange={(e) => handleNewInvoiceChange('scenarioId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="SN001">SN001 - Goods at Standard Rate to Registered Buyers</option>
                  <option value="SN002">SN002 - Goods at Standard Rate to Unregistered Buyers</option>
                  <option value="SN003">SN003 - Sale of Steel (Melted and Re-Rolled)</option>
                  <option value="SN004">SN004 - Sale by Ship Breakers</option>
                  <option value="SN008">SN008 - Sale of 3rd Schedule Goods</option>
                  <option value="SN009">SN009 - Cotton Spinners Purchase from Cotton Ginners (Textile Sector)</option>
                  <option value="SN010">SN010 - Mobile Operators Add Sales (Telecom Sector)</option>
                  <option value="SN011">SN011 - Toll Manufacturing Sale by Steel Sector</option>
                  <option value="SN012">SN012 - Sale of Petroleum products</option>
                  <option value="SN013">SN013 - Electricity Supply to Retailers</option>
                  <option value="SN014">SN014 - Sale of Gas to CNG Stations</option>
                  <option value="SN015">SN015 - Sale of Mobile Phones</option>
                  <option value="SN018">SN018 - Sale of Services where FED is Charged in ST Mode</option>
                  <option value="SN019">SN019 - Sale of Services</option>
                  <option value="SN020">SN020 - Sale of Electric Vehicles</option>
                  <option value="SN021">SN021 - Sale of Cement /Concrete Block</option>
                  <option value="SN022">SN022 - Sale of Potassium Chlorate</option>
                  <option value="SN023">SN023 - Sale of CNG</option>
                  <option value="SN025">SN025 - Drugs Sold at Fixed ST Rate Under Serial 81 of Eighth Schedule Table 1</option>
                  <option value="SN026">SN026 - Sale to End Consumer by Retailers</option>
                  <option value="SN027">SN027 - Sale to End Consumer by Retailers</option>
                  <option value="SN028">SN028 - Sale to End Consumer by Retailers</option>
                </select>
              </div>


            </div>

            {/* Items Section */}
            <div className="mb-6 invoice-items-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Invoice Items</h3>
                <button
                  onClick={addNewItem}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors duration-200"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Item
                </button>
              </div>

              <div className="table-container">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        HS Code
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product Description
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        UoM
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Value Excl. ST
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sales Tax
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Value
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {newInvoice.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-2 py-2">
                          <div className="hs-dropdown-container hs-code-dropdown">
                            <div className="relative">
                              <input
                                type="text"
                                value={hsCodeSearchTerms[index] || item.hsCode}
                                onChange={(e) => handleHsCodeSearch(index, e.target.value)}
                                onFocus={() => handleHsCodeSearch(index, hsCodeSearchTerms[index] || item.hsCode)}
                                onBlur={() => {
                                  // Ensure the HS Code is saved when user finishes typing
                                  const currentValue = hsCodeSearchTerms[index] || item.hsCode;
                                  if (currentValue && currentValue !== item.hsCode) {
                                    handleItemChange(index, 'hsCode', currentValue);
                                  }
                                }}
                                className="w-full px-1 py-1 pr-6 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                placeholder="Search HS Code"
                              />
                              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                <span className="text-gray-400 text-xs">🔍</span>
                              </div>
                            </div>
                            {showHsCodeDropdowns[index] && (hsCodeSearchLoading[index] || filteredHsCodes[index]) && (
                              <div
                                className="hs-dropdown-menu overflow-y-auto"
                                style={{
                                  position: 'absolute',
                                  zIndex: 9999,
                                  backgroundColor: 'white',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                  maxHeight: '300px',
                                  width: '350px',
                                  top: '100%',
                                  left: '0',
                                  marginTop: '2px'
                                }}
                              >
                                <div className="p-2 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                                  <div className="text-xs font-medium text-gray-700">HS Code Search Results</div>
                                  <button
                                    onClick={() => setShowHsCodeDropdowns(prev => ({ ...prev, [index]: false }))}
                                    className="text-gray-400 hover:text-gray-600 text-lg font-bold"
                                  >
                                    ×
                                  </button>
                                </div>
                                {hsCodeSearchLoading[index] ? (
                                  <div className="px-4 py-8 text-center">
                                    <div className="inline-flex items-center">
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                      <span className="text-sm text-gray-600">Searching HS Codes...</span>
                                    </div>
                                  </div>
                                ) : filteredHsCodes[index] && filteredHsCodes[index].length > 0 ? (
                                  filteredHsCodes[index].map((hs) => (
                                    <div
                                      key={hs.code}
                                      onClick={() => selectHsCode(index, hs)}
                                      className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    >
                                      <div className="font-medium text-sm text-blue-600 mb-1">{hs.code}</div>
                                      <div className="text-sm text-gray-700 leading-relaxed">{hs.description}</div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="px-4 py-8 text-center text-sm text-gray-500">
                                    No HS codes found matching your search.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <div className="relative group">
                            <input
                              type="text"
                              value={item.productDescription}
                              onChange={(e) => handleItemChange(index, 'productDescription', e.target.value)}
                              className="w-full px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Product description"
                              title={item.productDescription}
                            />
                            {item.productDescription && item.productDescription.length > 20 && (
                              <div className="absolute z-20 invisible group-hover:visible bg-gray-800 text-white text-xs rounded py-2 px-3 bottom-full left-0 mb-1 w-64 shadow-lg">
                                <div className="font-medium mb-1">Full Description:</div>
                                <div className="break-words">{item.productDescription}</div>
                                <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <select
                            value={item.uoM}
                            onChange={(e) => handleItemChange(index, 'uoM', e.target.value)}
                            className="w-full px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">UoM</option>
                            {uomCodes.map((uom) => (
                              <option key={uom.code} value={uom.code}>
                                {uom.code}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="w-full px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            min="1"
                            step="0.01"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            value={item.valueSalesExcludingST}
                            onChange={(e) => handleItemChange(index, 'valueSalesExcludingST', e.target.value)}
                            className="w-full px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            value={item.salesTaxApplicable}
                            onChange={(e) => handleItemChange(index, 'salesTaxApplicable', e.target.value)}
                            className="w-full px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <span className="text-xs font-medium text-gray-900">
                            {(parseFloat(item.totalValues) || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          {newInvoice.items.length > 1 && (
                            <button
                              onClick={() => removeItem(index)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remove item"
                            >
                              <TrashIcon className="h-3 w-3" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowNewInvoiceForm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveInvoice}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-2" />
                Save Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoices List */}
      <div className="bg-white rounded-lg shadow-lg invoice-table-container">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {searchFilters.transactionType === 'SALES' ? 'Domestic Sales Invoices' :
               searchFilters.transactionType === 'PURCHASES' ? 'Domestic Purchases Invoices' :
               'Domestic Sales and Purchases Invoices'}
              <span className="text-sm text-gray-500 ml-2">
                ({invoices.length} invoices{invoices.length > itemsPerPage ? `, showing ${startIndex + 1}-${Math.min(endIndex, invoices.length)}` : ''})
              </span>
            </h2>
            <button
              onClick={handleSearch}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              )}
              Search Invoices
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sr No.
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice No
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Buyer Registration No / CNIC
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Buyer Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Seller Registration No / CNIC
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Seller Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sale Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2">Loading invoices...</span>
                    </div>
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                    No invoices found
                  </td>
                </tr>
              ) : (
                currentInvoices.map((invoice, index) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{startIndex + index + 1}</td>
                    <td className="px-4 py-3">{getStatusBadge(invoice.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{invoice.invoice_ref_no || invoice.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{invoice.invoice_type || 'SALE_INVOICE'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{invoice.buyer_ntn_cnic || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{invoice.buyer_business_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{invoice.seller_ntn_cnic || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{invoice.seller_business_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {invoice.sale_type || 'Goods at standard rate (default)'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleViewInvoice(invoice)}
                          className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                          title="View"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>

                        {/* FBR Validate Button */}
                        {canValidateInvoice(invoice) && (
                          <button
                            onClick={() => handleFBRValidate(invoice.id)}
                            disabled={actionLoading[`validate_${invoice.id}`]}
                            className="text-yellow-600 hover:text-yellow-800 p-1 rounded hover:bg-yellow-50 disabled:opacity-50"
                            title="Validate with FBR"
                          >
                            {actionLoading[`validate_${invoice.id}`] ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                            ) : (
                              <ShieldCheckIcon className="h-4 w-4" />
                            )}
                          </button>
                        )}

                        {/* FBR Submit Button */}
                        {canSubmitInvoice(invoice) && (
                          <button
                            onClick={() => handleFBRSubmit(invoice.id)}
                            disabled={actionLoading[`submit_${invoice.id}`]}
                            className="text-purple-600 hover:text-purple-800 p-1 rounded hover:bg-purple-50 disabled:opacity-50"
                            title="Submit to FBR"
                          >
                            {actionLoading[`submit_${invoice.id}`] ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                            ) : (
                              <PaperAirplaneIcon className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {invoices.length > itemsPerPage && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              {/* Pagination Info */}
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                <span className="font-medium">{Math.min(endIndex, invoices.length)}</span> of{' '}
                <span className="font-medium">{invoices.length}</span> invoices
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center space-x-2">
                {/* Previous Button */}
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                    // Adjust start page if we're near the end
                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }

                    // Add first page and ellipsis if needed
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => handlePageChange(1)}
                          className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="ellipsis1" className="px-2 py-2 text-sm text-gray-500">
                            ...
                          </span>
                        );
                      }
                    }

                    // Add visible page numbers
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          className={`relative inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md ${
                            i === currentPage
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }

                    // Add last page and ellipsis if needed
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="ellipsis2" className="px-2 py-2 text-sm text-gray-500">
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => handlePageChange(totalPages)}
                          className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}
                </div>

                {/* Next Button */}
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* JSON Response Modal */}
      {jsonModal.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setJsonModal({ ...jsonModal, show: false })}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {jsonModal.title}
                  </h3>
                  {jsonModal.isDevelopmentMode && (
                    <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                      Development Mode
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setJsonModal({ ...jsonModal, show: false })}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {jsonModal.isDevelopmentMode && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Development Mode Response
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>This is a mock response because your FBR API credentials are not configured or invalid.</p>
                        <p className="mt-1">
                          Please update your FBR API configuration in{' '}
                          <button
                            onClick={() => navigate('/app/profile')}
                            className="underline hover:text-yellow-900"
                          >
                            Profile Settings
                          </button>
                          {' '}for real FBR API responses.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Show warning for malformed responses */}
              {jsonModal.content && jsonModal.content.includes('"statusCode": "ERROR"') && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        FBR API Response Issue Detected
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>The FBR API returned a malformed or invalid response. This has been automatically handled by our system.</p>
                        <p className="mt-1">
                          <strong>Possible causes:</strong>
                        </p>
                        <ul className="mt-1 list-disc list-inside">
                          <li>FBR API server issues or maintenance</li>
                          <li>Invalid or expired API credentials</li>
                          <li>Network connectivity problems</li>
                        </ul>
                        <p className="mt-2">
                          Please check your FBR API configuration or try again later.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                  {jsonModal.content}
                </pre>
              </div>

              <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(jsonModal.content);
                    toast.success('Response copied to clipboard');
                  }}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Copy to Clipboard
                </button>
                <button
                  type="button"
                  onClick={() => setJsonModal({ ...jsonModal, show: false })}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice View Modal */}
      <InvoiceViewModalNew
        key={selectedInvoice?.id || 'no-invoice'}
        invoice={selectedInvoice}
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedInvoice(null);
        }}
      />

      {/* Upload Review Modal */}
      {showUploadReview && uploadId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative min-h-screen">
            <InvoiceDataReview
              uploadId={uploadId}
              isManualMode={isManualMode}
              onCreateInvoice={(invoice) => {
                setShowUploadReview(false);
                setUploadId(null);
                setIsManualMode(false);
                toast.success('Invoice created successfully from uploaded data!');
                // Navigate to invoices list instead of staying on compact form
                navigate('/app/invoices', { replace: true });
              }}
              onCancel={() => {
                setShowUploadReview(false);
                setUploadId(null);
                setIsManualMode(false);
                // Navigate to remove the uploadId parameter
                navigate('/app/invoice/compact', { replace: true });
              }}
            />
          </div>
        </div>
      )}

      {/* Buyer Search Modal */}
      {showBuyerModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Search Buyers</h3>
                <button
                  onClick={() => {
                    setShowBuyerModal(false);
                    setShowBuyerDropdown(false);
                    setBuyerSearchResults([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search by business name or NTN/CNIC..."
                  onChange={(e) => searchBuyers(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
                {buyerSearchLoading && (
                  <div className="flex items-center justify-center mt-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-sm text-gray-600">Searching...</span>
                  </div>
                )}
              </div>

              {/* Search Results */}
              {showBuyerDropdown && buyerSearchResults.length > 0 && (
                <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md">
                  <div className="p-2 text-xs text-gray-500 border-b bg-gray-50">
                    Found {buyerSearchResults.length} customer(s). Click to select:
                  </div>
                  {buyerSearchResults.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => selectBuyerFromDropdown(customer)}
                      className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{customer.business_name}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            {customer.ntn_cnic && (
                              <span className="inline-block mr-4">NTN/CNIC: {customer.ntn_cnic}</span>
                            )}
                            {customer.province && (
                              <span className="inline-block">Province: {customer.province}</span>
                            )}
                          </div>
                          {customer.address && (
                            <div className="text-xs text-gray-500 mt-1">{customer.address}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showBuyerDropdown && buyerSearchResults.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  No customers found. Try a different search term.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default CompactInvoiceForm;
